import { createFileRoute, Link } from '@tanstack/react-router'
import { IconArrowLeftOutline18 } from 'nucleo-ui-outline-18'
import { ArticleBody } from '~/components/help/article-body'
import { MarketingShell } from '~/components/marketing/marketing-shell'
import { monolingualPageHead } from '~/lib/site-meta'
import { helpArticleBySlug, helpArticlesIn } from '~convex/lib/help'

/**
 * One help article (#120).
 *
 * No loader: the corpus is a static import, so both `head` and the component
 * look the article up synchronously. A loader would add a typed async boundary
 * around a plain array lookup and buy nothing — and the page still prerenders
 * to real HTML with its own title and description, which is the reason help
 * lives on the public site at all.
 *
 * An unknown slug renders a friendly dead end rather than throwing. Prerender
 * only follows links from the hub, so the only way here is a hand-typed or
 * stale URL, and someone in that position is better served by a way back than
 * by a 404.
 */
export const Route = createFileRoute('/ajuda/$slug')({
  component: HelpArticlePage,
  head: ({ params }) => {
    const article = helpArticleBySlug(params.slug)
    if (article === undefined) {
      return monolingualPageHead({
        description: 'Central de ajuda do Planno.',
        locale: 'pt-BR',
        path: `/ajuda/${params.slug}`,
        title: 'Ajuda | Planno',
      })
    }
    return monolingualPageHead({
      description: article.summary,
      locale: 'pt-BR',
      path: `/ajuda/${article.slug}`,
      title: `${article.title} | Ajuda do Planno`,
    })
  },
})

const BackToHub = () => (
  <Link
    className="inline-flex items-center gap-1.5 text-[#77777f] text-sm transition-colors hover:text-[#0b0b0d]"
    to="/ajuda"
  >
    <IconArrowLeftOutline18 className="size-4" />
    Central de ajuda
  </Link>
)

function HelpArticlePage() {
  const { slug } = Route.useParams()
  const article = helpArticleBySlug(slug)

  if (article === undefined) {
    return (
      <MarketingShell>
        <div className="mx-auto w-full max-w-[44rem] px-5 pt-14 pb-24 sm:px-8">
          <BackToHub />
          <h1 className="pt-6 font-semibold text-[#0b0b0d] text-[2rem] leading-[1.15] tracking-[-0.02em]">
            Não encontramos esta página
          </h1>
          <p className="pt-4 text-[#57575f] text-[1.0625rem] leading-relaxed">
            O endereço pode ter mudado. Veja a{' '}
            <Link className="underline underline-offset-2" to="/ajuda">
              central de ajuda
            </Link>{' '}
            para encontrar o que procura.
          </p>
        </div>
      </MarketingShell>
    )
  }

  // Siblings in the same category — the useful "what next" for someone who
  // arrived from a search engine rather than from the hub.
  const siblings = helpArticlesIn(article.category).filter((row) => row.slug !== article.slug)

  return (
    <MarketingShell>
      <article className="mx-auto w-full max-w-[44rem] px-5 pt-14 pb-24 sm:px-8">
        <BackToHub />

        <h1 className="pt-6 font-semibold text-[#0b0b0d] text-[2rem] leading-[1.15] tracking-[-0.02em] sm:text-[2.5rem]">
          {article.title}
        </h1>
        <p className="pt-4 pb-10 text-[#57575f] text-[1.125rem] leading-relaxed">
          {article.summary}
        </p>

        <ArticleBody body={article.body} />

        {siblings.length > 0 && (
          <footer className="mt-16 border-black/10 border-t pt-8">
            <h2 className="pb-4 font-medium text-[#77777f] text-sm">Continue lendo</h2>
            <ul className="space-y-2">
              {siblings.map((row) => (
                <li key={row.slug}>
                  <Link
                    className="text-[#0b0b0d] underline underline-offset-2 hover:opacity-70"
                    params={{ slug: row.slug }}
                    to="/ajuda/$slug"
                  >
                    {row.title}
                  </Link>
                </li>
              ))}
            </ul>
          </footer>
        )}
      </article>
    </MarketingShell>
  )
}
